/**
 * DiscordEmbedPreview - hand-rolled pixel-perfect Discord embed renderer.
 *
 * Locked CSS values (verified against current Discord dark theme - RESEARCH Pattern 5):
 *   bg                #2b2d31
 *   left bar          4px wide, 100% height, color = embed.color (default BASTN_GOLD)
 *   border-radius     4px on right side; left edge flush with bar
 *   max-width         520px (Discord caps embed width)
 *   padding           0.5rem 1rem 1rem 0.75rem
 *   font-family       Newsreader fallback (gg sans NOT bundled - Discord IP)
 *   color (default)   #dbdee1
 *   title             16px / weight 600 / color #f2f3f5
 *   title link        color #00a8fc / text-decoration underline
 *   description       14px / line-height 1.4 / color #dbdee1 / pre-wrap
 *   thumbnail         80x80 / object-fit cover / border-radius 4px / top-right via grid
 *   image             100% width / max-height 350px / object-fit cover / margin-top 0.5rem
 *   footer            12px / color #b5bac1 / margin-top 0.5rem
 *   footer-icon       16x16 / border-radius 50% / margin-right 0.5rem
 *
 * Phase 4's slash command form mounts this VERBATIM - keep the props shape stable
 * and document any value tweaks in this comment block (don't drift silently).
 *
 * RESEARCH Anti-Patterns observed:
 *   - DO NOT bundle gg-sans (Discord IP). Use Newsreader fallback.
 *   - DO NOT use a vendor library like @skyra/discord-components-react. Hand-roll
 *     wins for BASTN theme integration.
 *   - DO NOT debounce useWatch in EmbedEditor - the preview must update on every
 *     keystroke (CONTEXT lock).
 *   - DO NOT lazy-load this component - synchronous render is required for the
 *     keystroke-update guarantee.
 */

import type { Embed } from '@guardian/shared';
import { BASTN_GOLD } from '@guardian/shared';
import { intToHex } from '@/lib/color';

export interface DiscordEmbedPreviewProps {
  embed: Embed;
}

export function DiscordEmbedPreview({ embed }: DiscordEmbedPreviewProps) {
  const colorHex =
    typeof embed.color === 'number'
      ? intToHex(embed.color)
      : intToHex(BASTN_GOLD);
  const hasUrl = Boolean(embed.url && embed.url.length > 0);
  const hasTitle = Boolean(embed.title && embed.title.length > 0);
  const hasDescription = Boolean(
    embed.description && embed.description.length > 0,
  );
  const hasThumbnail = Boolean(embed.thumbnail?.url);
  const hasImage = Boolean(embed.image?.url);
  const hasFooter = Boolean(embed.footer);

  return (
    <article
      data-testid="discord-embed-preview"
      data-slot="discord-embed-preview"
      style={{
        backgroundColor: '#2b2d31',
        borderLeft: `4px solid ${colorHex}`,
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
        maxWidth: '520px',
        padding: '0.5rem 1rem 1rem 0.75rem',
        fontFamily: "'Newsreader', system-ui, sans-serif",
        color: '#dbdee1',
        display: 'grid',
        gridTemplateColumns: '1fr 80px',
        gridTemplateRows: 'auto auto',
        columnGap: '1rem',
        rowGap: '0.5rem',
      }}
    >
      {hasTitle &&
        (hasUrl ? (
          <a
            href={embed.url}
            target="_blank"
            rel="noreferrer"
            data-testid="discord-embed-preview-title"
            style={{
              fontWeight: 600,
              fontSize: '1rem',
              lineHeight: 1.375,
              color: '#00a8fc',
              textDecoration: 'underline',
              gridColumn: '1 / 2',
              gridRow: 1,
            }}
          >
            {embed.title}
          </a>
        ) : (
          <span
            data-testid="discord-embed-preview-title"
            style={{
              fontWeight: 600,
              fontSize: '1rem',
              lineHeight: 1.375,
              color: '#f2f3f5',
              gridColumn: '1 / 2',
              gridRow: 1,
            }}
          >
            {embed.title}
          </span>
        ))}

      {hasDescription && (
        <div
          data-testid="discord-embed-preview-description"
          style={{
            fontSize: '0.875rem',
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            color: '#dbdee1',
            gridColumn: '1 / 2',
            gridRow: 2,
          }}
        >
          {embed.description}
        </div>
      )}

      {hasThumbnail && (
        <img
          src={embed.thumbnail!.url}
          width={80}
          height={80}
          alt=""
          data-testid="discord-embed-preview-thumbnail"
          style={{
            width: 80,
            height: 80,
            objectFit: 'cover',
            borderRadius: 4,
            gridColumn: 2,
            gridRow: '1 / 3',
          }}
        />
      )}

      {hasImage && (
        <img
          src={embed.image!.url}
          alt=""
          data-testid="discord-embed-preview-image"
          style={{
            width: '100%',
            maxHeight: 350,
            objectFit: 'cover',
            borderRadius: 4,
            marginTop: '0.5rem',
            gridColumn: '1 / 3',
          }}
        />
      )}

      {hasFooter && (
        <div
          data-testid="discord-embed-preview-footer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.75rem',
            color: '#b5bac1',
            marginTop: '0.5rem',
            gridColumn: '1 / 3',
          }}
        >
          {embed.footer!.icon_url && (
            <img
              src={embed.footer!.icon_url}
              width={16}
              height={16}
              alt=""
              data-testid="discord-embed-preview-footer-icon"
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
              }}
            />
          )}
          <span>{embed.footer!.text}</span>
        </div>
      )}
    </article>
  );
}
